import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Navigate,
  Route,
  BrowserRouter as Router,
  Routes,
} from "react-router-dom";
import useSocketConnect from "./hooks/useSocket.js";
import useSessionManager from "./hooks/useSessionManager.js";

import { getCurrentUser } from "./redux/slices/authSlice";
import { fetchMyPermissions } from "./redux/slices/userSlice";

import AuthenticatorPage from "./pages/authentication/Authenticator/Authenticator.js";
import LoginPage from "./pages/authentication/Login/Login.js";
import LoginSuccess from "./components/authentication/Login/LoginSuccess.js";
import Home from "./pages/user/Home/Home";

//Admin
import MessagesPage from "./pages/Superadmin/Messages/Messages";
import ManageProjects from "./components/projects/ManageProjects/ManageProjects";
import DelegationTable from "./components/tables/DelegationTable/DelegationTable.js";
import LandingPage from "./pages/Superadmin/B2BAdmin/B2BAdmin";
import ManageAbsencesAdmin from "./pages/Superadmin/ManageAbsences/ManageAbsences";
import ManageLeaves from "./pages/Superadmin/ManageLeave/ManageLeave";
import NotificationCenter from "./pages/Superadmin/ManageNotifications/ManageNotifications";
import OrganizationAdmin from "./pages/Superadmin/OrganizationAdmin/OrganizationAdmin";
import OrganizationSettings from "./pages/Superadmin/OrganizationSettings/OrganizationSettings";
import ManageUserAbsences from "./pages/user/calendar/ManageUserAbsences/ManageUserAbsences";
import OrganizationPage from "./pages/Organization/OrganizationPage.js";
import DelegationsTab from "./components/organization/Tabs/DelegationsTab";
import DelegationDetailsPage from './pages/Delegations/DelegationDetailsPage';
import TimeDistribution from "./pages/TimeDistribution/TimeDistribution";
//User
import ProjectPage from "./components/projects/ProjectPage/ProjectPage.js";
import Clock from "./pages/clock/Clock/Clock.js";
import ActionsPermissionsCRUD from "./pages/permissions/ActionsPermissionsCRUD/ActionsPermissionsCRUD.js";
import CalendarDesktopPage from "./pages/Superadmin/ShiftsDesktopPage/ShiftsDesktopPage.js";
import AbsencesPage from "./pages/user/Absences/Absences";
import AnnualLeaveUser from "./pages/user/AnnualLeaveUser/AnnualLeaveUser";
import CalendarPage from "./pages/user/Calendar/Calendar";
import ManageDocuments from "./pages/user/Documents/Documents";
import Employees from "./pages/user/EmployeesPage/EmployeesPage";
import NotificationsPage from "./pages/user/Notifications/Notifications";
import ProfilePage from "./pages/user/Profile/Profile";
import RequestAbsences from "./pages/user/RequestAbsencesMobile/RequestAbsencesMobile";
import EmployeeProfile from "./pages/user/EmployeesPage/EmployeesPage.js";
import UserShiftsPage from "./pages/user/UserShifts/UserShifts";

import ResponsiveLayout from "./layouts/ResponsiveLayout/ResponsiveLayout";
import { ROLES } from "./utils/constants/roles.js";
import { isTokenExpired } from "./utils/session.js";

const ProtectedRoute = ({ children, requiredRole }) => {

  const { isAuthenticated, user, isLoading, token } = useSelector((state) => state.auth);

  if (isLoading) {
    return <div>Loading user info...</div>;
  }

  if (!isAuthenticated || !token || isTokenExpired(token)) {
    console.log("User is not authenticated, redirecting to login.");
    return <Navigate to="/login" replace />;
  }

  if (requiredRole && user?.role?.name !== requiredRole) {
    console.log(`User does not have required role (${requiredRole}), redirecting to dashboard.`);
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

const _AdminOnly = ({ children }) => {
  return (
    <ProtectedRoute requiredRole={ROLES.SUPER_ADMIN}>
      {children}
    </ProtectedRoute>
  );
};

// AdminOnly component defined but not exported as default


function App() {

  const dispatch = useDispatch();
  const { token, isAuthenticated, isLoading } = useSelector(
    (state) => state.auth,
  );
  const isSessionActive = isAuthenticated && token && !isTokenExpired(token);

  useSessionManager();

  useEffect(() => {
    if (token) {
      dispatch(getCurrentUser());
      dispatch(fetchMyPermissions());
    }
  }, [token, dispatch]);

  useSocketConnect();

  if (isLoading) {
    return <div>Loading...</div>;
  }

  return (
    <Router>
      <Routes>

        <Route
          path="/"
          element={
            isSessionActive ? (
              <Navigate to="/dashboard" replace />
            ) : (
              <Navigate to="/login" replace />
            )
          }
        />

        <Route
          path="/login"
          element={
            isSessionActive ? <Navigate to="/dashboard" replace /> : <LoginPage />
          }
        />

        <Route path="/login/success" element={<LoginSuccess />} />
        <Route path="/authenticator" element={<AuthenticatorPage />} />

        <Route
          element={
            <ProtectedRoute>
              <ResponsiveLayout />
            </ProtectedRoute>
          }
        >

          <Route path="/dashboard" element={<Home />} />
          <Route path="/notifications" element={<NotificationsPage />} />
          <Route path="/profile" element={<ProfilePage />} />
          <Route path="/calendar" element={<CalendarPage />} />
          <Route path="/absences" element={<AbsencesPage />} />
          <Route path="/documents" element={<ManageDocuments />} />
          <Route path="/employees" element={<Employees />} />
          <Route path="/annual-leave" element={<AnnualLeaveUser />} />
          <Route path="/absence-request" element={<RequestAbsences />} />
          <Route path="/user-shifts" element={<UserShiftsPage />} />
          <Route path="/projects" element={<ManageProjects />} />
          <Route path="/projects/:id" element={<ProjectPage />} />
          <Route path="/clock" element={<Clock />} />

          {/* <Route element={<AdminOnly />}> */}
            <Route path="/b2b-admin" element={<LandingPage />} />

            {/* Users / organization */}
            <Route path="organization" element={<OrganizationPage />} />
            <Route path="/organization/employee/:id" element={<EmployeeProfile />} />
            <Route path="/organizationadmin" element={<OrganizationAdmin />} />
            <Route path="/organization/settings" element={<OrganizationSettings />} />
            <Route path="/organization/settings/permissions" element={<ProtectedRoute requiredRole={ROLES.SUPER_ADMIN}><OrganizationSettings /></ProtectedRoute>} />
            <Route path="/organization/settings/documents" element={<ProtectedRoute requiredRole={ROLES.SUPER_ADMIN}><OrganizationSettings /></ProtectedRoute>} />
            <Route path="/delegation-table" element={<DelegationsTab />} />
            <Route path="/organization/delegations/:id" element={<DelegationDetailsPage />} />
            <Route path="/time-distribution" element={<TimeDistribution />} />
            {/* Permissions */}
            <Route path="/manage-permissions/:id" element={<ProtectedRoute requiredRole={ROLES.SUPER_ADMIN}><ActionsPermissionsCRUD /></ProtectedRoute>} />

            {/* Documents */}
            <Route path="/admindocuments" element={<ManageDocuments />} />
            <Route path="/folders/:id" element={<Navigate to="/documents" replace />} />
            <Route path="/folder-files" element={<Navigate to="/documents" replace />} />

            {/* Absences / leaves */}
            <Route path="/manage-leaves" element={<ManageLeaves />} />
            <Route path="/manage-absences-admin" element={<ManageAbsencesAdmin />} />
            <Route path="/absences-pc" element={<ManageUserAbsences />} />

            {/* Shifts */}
            <Route path="/shiftsDesktop" element={<CalendarDesktopPage />} />

            {/* Notifications / messages */}
            <Route path="/messages" element={<MessagesPage />} />
            <Route path="/notifications-pc" element={<NotificationCenter />} />
            {/* Delegations */}
            <Route path="/delegation-table" element={<DelegationTable />} />
          {/* </Route> */}

          <Route path="*" element={<Navigate to="/dashboard" replace />} />
        </Route>

        <Route path="*" element={<Navigate to={isSessionActive ? "/dashboard" : "/login"} replace />} />
      </Routes>
    </Router>
  );
}

export default App;
